import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { AppType, ConfigurationService } from '@treasury/core/config';
import { CompanyUserService } from '@treasury/domain/company/user';
import { CompanyUserProductFeatureSummary } from '@treasury/domain/company/user/entities';
import '@treasury/presentation/components/tm-side-nav/side-nav.component';
import { TmBoMenuItem } from '@treasury/presentation/components/tm-side-nav/side-nav.types';
import { TmWizardStep } from '@treasury/presentation/components/tm-wizard';
import { TmSideDrawerService } from '@treasury/presentation/services/side-drawer';
import { TmToastPosture, TmToastService } from '@treasury/presentation/services/toast';
import { html, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './partials/products/account-reconciliation.component';
import './partials/products/ach.component';
import './partials/products/integrated-services.component';
import './partials/products/positive-pay/positive-pay.component';
import './partials/products/reporting.component';
import './partials/products/stop-payment.component';
import './partials/products/transfer-loan-payment.component';
import './partials/products/wires/wire.component';

enum MenuItem {
    Ach = 'ACH',
    Wire = 'Wire',
    PositivePay = 'Positive Pay',
    TransferLoanPayment = 'Transfer/loan payment',
    AccountReconciliation = 'Account reconciliation',
    StopPayment = 'Stop payment',
    IntegratedServices = 'Integrated Services',
    InformationReporting = 'Reporting',
    FxWire = 'FX Wire',
    Receivables = 'Receivables',
}

interface TmBoMenuItemWithComponent extends TmBoMenuItem {
    component: () => TemplateResult;
}

@customElement('tm-company-user-limits-and-entitlements')
export class CompanyUserLimitsAndEntitlements extends TmWizardStep {
    public stepLabel = 'Limits and Entitlements';

    @property({ type: Number })
    public companyId?: number;

    @property({ type: Number })
    public userId?: number;

    @property({ type: Object })
    public productFeatureSummary?: CompanyUserProductFeatureSummary;

    @InjectProperty()
    declare private readonly config: ConfigurationService;

    @InjectProperty()
    declare private readonly companyUserService: CompanyUserService;

    @InjectProperty()
    declare private readonly drawerService: TmSideDrawerService;

    @InjectProperty()
    declare private readonly toastService: TmToastService;

    @state()
    private loading = true;

    @state()
    private selectedMenuItem? = this.menuItems[0];

    private get isBoApp() {
        return this.config.app === AppType.BackOffice;
    }

    private get menuItems() {
        const menuItems: Array<TmBoMenuItemWithComponent> = [];

        if (this.productFeatureSummary?.hasAch) {
            menuItems.push({
                title: MenuItem.Ach,
                component: () =>
                    html`<tm-bo-limits-and-entitlements-ach
                        .companyId=${this.companyId}
                        .userId=${this.userId}
                    ></tm-bo-limits-and-entitlements-ach>`,
            });
        }

        if (this.productFeatureSummary?.hasWireTransfers) {
            menuItems.push({
                title: MenuItem.Wire,
                component: () =>
                    html`<tm-bo-limits-and-entitlements-wire
                        .companyId=${this.companyId}
                        .userId=${this.userId}
                    ></tm-bo-limits-and-entitlements-wire>`,
            });
        }

        if (this.productFeatureSummary?.hasWireTransfersDli) {
            menuItems.push({
                title: MenuItem.FxWire,
                component: () => html`<div>FX Wires Coming Soon</div>`,
            });
        }

        if (this.productFeatureSummary?.hasInformationReporting) {
            menuItems.push({
                title: MenuItem.InformationReporting,
                component: () =>
                    html`<tm-bo-limits-and-entitlements-reporting
                        .companyId=${this.companyId}
                        .userId=${this.userId}
                    ></tm-bo-limits-and-entitlements-reporting>`,
            });
        }

        if (
            this.productFeatureSummary?.hasStandardPosPay ||
            this.productFeatureSummary?.hasSsoPosPay
        ) {
            menuItems.push({
                title: MenuItem.PositivePay,
                component: () =>
                    html`<tm-bo-limits-and-entitlements-positive-pay
                        .companyId=${this.companyId}
                        .userId=${this.userId}
                    ></tm-bo-limits-and-entitlements-positive-pay>`,
            });
        }

        if (this.productFeatureSummary?.hasInternalTransfers) {
            menuItems.push({
                title: MenuItem.TransferLoanPayment,
                component: () =>
                    html`<tm-bo-limits-and-entitlements-transfer-loan-payment
                        .companyId=${this.companyId}
                        .userId=${this.userId}
                    ></tm-bo-limits-and-entitlements-transfer-loan-payment>`,
            });
        }

        if (this.productFeatureSummary?.hasAccountReconciliationReporting) {
            menuItems.push({
                title: MenuItem.AccountReconciliation,
                component: () =>
                    html`<tm-bo-limits-and-entitlements-account-reconciliation
                        .companyId=${this.companyId}
                        .userId=${this.userId}
                    ></tm-bo-limits-and-entitlements-account-reconciliation>`,
            });
        }

        if (this.productFeatureSummary?.hasStopPayment) {
            menuItems.push({
                title: MenuItem.StopPayment,
                component: () =>
                    html`<tm-bo-limits-and-entitlements-stop-payment
                        .companyId=${this.companyId}
                        .userId=${this.userId}
                    ></tm-bo-limits-and-entitlements-stop-payment>`,
            });
        }

        if (this.productFeatureSummary?.hasReceivables) {
            menuItems.push({
                title: MenuItem.Receivables,
                component: () => html`<div>Receivables Coming Soon</div>`,
            });
        }

        if (this.productFeatureSummary?.hasIntegratedServices) {
            menuItems.push({
                title: MenuItem.IntegratedServices,
                component: () =>
                    html`<tm-bo-limits-and-entitlements-integrated-services
                        .companyId=${this.companyId}
                        .userId=${this.userId}
                    ></tm-bo-limits-and-entitlements-integrated-services>`,
            });
        }

        return menuItems;
    }

    public validate() {
        return true;
    }

    public render() {
        return html`<div class="flex flex-row gap-10">
            ${this.renderSideNav()}
            <div class="flex-1">${this.renderContent()}</div>
        </div>`;
    }

    protected async firstUpdated() {
        try {
            this.loading = true;
            if (this.isBoApp && !this.companyId) {
                throw new Error('Company ID is required');
            }
            if (!this.userId) {
                throw new Error('User ID is required');
            }

            this.productFeatureSummary =
                await this.companyUserService.getCompanyUserProductFeatureSummary(
                    this.userId,
                    this.companyId
                );
        } catch {
            this.toastService.show(
                'Error fetching limits and entitlements',
                TmToastPosture.Danger,
                true
            );
        } finally {
            this.loading = false;
        }
    }

    private renderSideNav() {
        return html`<tm-side-nav
            .menuItems=${this.menuItems}
            .selected=${this.selectedMenuItem}
            @selected=${this.handleMenuItemSelected}
        ></tm-side-nav>`;
    }

    private renderContent() {
        return html`${this.selectedMenuItem?.component()}`;
    }

    private handleMenuItemSelected(e: CustomEvent<TmBoMenuItem>) {
        this.selectedMenuItem = e.detail as TmBoMenuItemWithComponent;
    }
}
